import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Input } from '@components/forms/Input';
import {
  PublicApiError,
  PublicBooking,
  PublicSlot,
  formatInTimezone,
  publicBookingService,
} from './publicBookingService';

const SLOT_WINDOW_DAYS = 7;

const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const dayKey = (iso: string, timeZone: string) =>
  formatInTimezone(iso, timeZone, 'sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });

interface RescheduleSectionProps {
  token: string;
  timezone: string;
  onMoved: (booking: PublicBooking) => void;
  onClose: () => void;
  /** The booking may have changed on the server (e.g. it has started meanwhile). */
  onConflict: () => void;
  onGone: () => void;
}

const RescheduleSection: React.FC<RescheduleSectionProps> = ({
  token,
  timezone,
  onMoved,
  onClose,
  onConflict,
  onGone,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const today = useMemo(() => dayKey(new Date().toISOString(), timezone), [timezone]);

  const [dateFrom, setDateFrom] = useState(today);
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<PublicSlot | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(false);
    publicBookingService
      .getManageSlots(token, { dateFrom, days: SLOT_WINDOW_DAYS })
      .then(response => {
        if (id === requestId.current) setSlots(response.slots);
      })
      .catch(err => {
        if (id !== requestId.current) return;
        setSlots([]);
        if (err instanceof PublicApiError && err.status === 404) onGone();
        else setLoadError(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [token, dateFrom, reloadKey, onGone]);

  const slotsByDay = useMemo(() => {
    const groups = new Map<string, PublicSlot[]>();
    for (const slot of slots) {
      const key = dayKey(slot.start, timezone);
      groups.set(key, [...(groups.get(key) ?? []), slot]);
    }
    return Array.from(groups.entries());
  }, [slots, timezone]);

  const changeDateFrom = (value: string) => {
    if (!value) return;
    setDateFrom(value < today ? today : value);
    setSelected(null);
    setError('');
  };

  const timeLabel = (slot: PublicSlot) =>
    formatInTimezone(slot.start, timezone, locale, { hour: '2-digit', minute: '2-digit' });

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const moved = await publicBookingService.rescheduleBooking(token, selected.start);
      onMoved(moved);
    } catch (err) {
      if (err instanceof PublicApiError && err.status === 404) {
        onGone();
      } else if (err instanceof PublicApiError && err.code === 'profile_inactive') {
        setError(t('publicBooking.manage.inactiveText'));
        setSelected(null);
      } else if (err instanceof PublicApiError && err.status === 409) {
        setError(t('publicBooking.manage.rescheduleSlotTaken'));
        setSelected(null);
        setReloadKey(key => key + 1);
        onConflict();
      } else if (err instanceof PublicApiError && err.code === 'start_in_past') {
        setError(t('publicBooking.manage.rescheduleStartInPast'));
        setSelected(null);
        setReloadKey(key => key + 1);
      } else {
        setError(t('publicBooking.manage.errorText'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="reschedule-title"
      className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4"
    >
      <div>
        <h2 id="reschedule-title" className="text-base font-semibold text-gray-900 dark:text-white">
          {t('publicBooking.manage.rescheduleTitle')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('publicBooking.manage.rescheduleHint')}
        </p>
      </div>

      <div className="flex items-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={dateFrom <= today}
          onClick={() => changeDateFrom(addDays(dateFrom, -SLOT_WINDOW_DAYS))}
        >
          {t('publicBooking.book.earlier')}
        </Button>
        <Input
          id="reschedule-date-from"
          type="date"
          label={t('publicBooking.book.dateFrom')}
          value={dateFrom}
          min={today}
          onChange={event => changeDateFrom(event.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => changeDateFrom(addDays(dateFrom, SLOT_WINDOW_DAYS))}
        >
          {t('publicBooking.book.later')}
        </Button>
      </div>

      {loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('publicBooking.loading')}</p>
      )}
      {loadError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t('publicBooking.book.errorText')}
        </p>
      )}
      {!loading && !loadError && slotsByDay.length === 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('publicBooking.book.noSlots')}
        </p>
      )}

      <div className="space-y-4">
        {slotsByDay.map(([day, daySlots]) => (
          <div key={day}>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {formatInTimezone(daySlots[0].start, timezone, locale, {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              {daySlots.map(slot => (
                <button
                  key={slot.start}
                  type="button"
                  aria-pressed={selected?.start === slot.start}
                  onClick={() => {
                    setSelected(slot);
                    setError('');
                  }}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    selected?.start === slot.start
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-primary-300'
                  }`}
                >
                  {timeLabel(slot)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
        >
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
          {t('publicBooking.manage.rescheduleBack')}
        </Button>
        <Button
          variant="gradient-multi"
          className="flex-1"
          onClick={handleConfirm}
          loading={saving}
          disabled={!selected || saving}
        >
          {selected
            ? t('publicBooking.manage.rescheduleConfirm', {
                date: formatInTimezone(selected.start, timezone, locale, {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                }),
                time: timeLabel(selected),
              })
            : t('publicBooking.manage.rescheduleChoose')}
        </Button>
      </div>
    </section>
  );
};

export { RescheduleSection };
